import { useMemo, useRef } from "react"
import { CreateAreaSidebar } from "components/ui/customWebviewer/shared/CreateAreaSidebar/CreateAreaSidebar"
import { FurnitureImage } from "components/ui/customWebviewer/FurnitureSidebar/layout/FurnitureImage/FurnitureImage"
import { ElevationReferenceSelect } from "components/ui/customWebviewer/FurnitureSidebar/layout/ElevationReferenceSelect/ElevationReferenceSelect"
import { ModelNumberSection } from "components/ui/customWebviewer/FurnitureSidebar/layout/ModelNumberSection/ModelNumberSection"
import { DimensionsSection } from "components/ui/customWebviewer/FurnitureSidebar/layout/DimensionsSection/DimensionsSection"
import { CategoriesSection } from "components/ui/customWebviewer/FurnitureSidebar/layout/CategoriesSection/CategoriesSection"
import { CustomizationSection } from "components/ui/customWebviewer/shared/CustomizationSection/CustomizationSection"
import { useGetModelNumber, useGetModelNumberImageLink } from "hooks/api/modelNumbers/modelNumbers.hook"
import { useGetSeries } from "hooks/api/series/series.hook"
import { useCreateFurnitureForm } from "./useCreateFurnitureForm.hook"
import type { TModelNumberTarget } from "components/ui/customWebviewer/FurnitureSidebar/layout/ModelNumberSection/SelectModelNumberModal/SelectModelNumberModal"
import type { TFurnitureCreateDetails } from "components/ui/customWebviewer/interactions/useFurnitureCreate"
import type { TEref } from "types/elevationReferences.type"
import type { TModelNumberDetails } from "types/modelNumbers.type"
import style from "./CreateFurnitureSidebar.module.scss"

const { body, divider } = style

// A cabinet's dimensions are inches of real cabinet: 0 is not a smaller object, it's a broken one.
const MIN_DIMENSION_IN = 1

// A brand-new object has no ML detections behind it — the picker's "ML choice" tags simply never
// apply here. Hoisted so the memo below isn't invalidated by a fresh array on every render.
const NO_DETECTED_MODEL_NUMBERS: string[] = []

type Props = {
	/**
	 * The area drawn on the drawing, or null while the user still has to draw one — which is the
	 * difference between the panel's two states: the instruction, or the form + footer.
	 */
	area: { pageNumber: number } | null
	/** Elevation references of the page the area was drawn on (erefs are page-bound). */
	pageErefs: TEref[]
	isPending?: boolean
	onCancel: () => void
	onCreate: (details: TFurnitureCreateDetails) => void
	onClose: () => void
}

/**
 * Right-panel companion of the "add cabinet" mode (REQ-293). Like its elevation-reference sibling
 * (`CreateErefSidebar`) it has exactly two states: before an area is drawn it only explains the
 * gesture, and once one exists it asks for the object's details.
 *
 * The form is deliberately the furniture EDIT sidebar's sections, in the design's order and with
 * nothing else: image, elevation reference, model number, dimensions, categories. The sections that
 * only make sense for a saved object — attachments, notes, price/name, the reviewed/quotation
 * footer, the connected-callouts tab — are not part of creation; the user gets them the moment the
 * object is created, because Create hands the panel over to that sidebar.
 */
export const CreateFurnitureSidebar = ({
	area,
	pageErefs,
	isPending = false,
	onCancel,
	onCreate,
	onClose,
}: Props) => {
	const form = useCreateFurnitureForm(pageErefs)
	const { categorySelection } = form
	const selectedCategoryId = categorySelection.selectedModelCategoryId

	// The picker is driven by the form rather than by a saved furniture: the category scopes its
	// own tab, the dimensions score each candidate's match, and with no category chosen the picker
	// opens on its catalogue-wide tab instead (REQ-398). A pick made there brings its category with
	// it — see `selectModelNumber`.
	const modelNumberTarget = useMemo<TModelNumberTarget>(
		() => ({
			category:
				selectedCategoryId === null
					? null
					: {
							id: selectedCategoryId,
							name: String(
								categorySelection.modelCategoryOptions.find((option) => option.value === selectedCategoryId)?.label ??
									"",
							),
							model_label: "",
						},
			// The two upper levels are names in this form — `useCategorySelection` keys the taxonomy by
			// name — and the picker only reads `.name` off them, for the current-category notice. The
			// ids it never looks at are filled with 0, as the edit sidebar already does when it builds
			// an updated furniture from the same option lists.
			super_category:
				categorySelection.selectedSuperCategory === null
					? null
					: { id: 0, name: String(categorySelection.selectedSuperCategory) },
			top_level_category:
				categorySelection.selectedTopLevelCategory === null
					? null
					: { id: 0, name: String(categorySelection.selectedTopLevelCategory) },
			detected_model_numbers: NO_DETECTED_MODEL_NUMBERS,
			selected_model_number: form.selectedModelNumber,
			width: form.width,
			height: form.height,
			depth: form.depth,
		}),
		[
			selectedCategoryId,
			categorySelection.modelCategoryOptions,
			categorySelection.selectedSuperCategory,
			categorySelection.selectedTopLevelCategory,
			form.selectedModelNumber,
			form.width,
			form.height,
			form.depth,
		],
	)

	// The series the picked elevation prices against — the same warning the edit sidebar shows, so a
	// model number with no price in that series is flagged before the object exists rather than after.
	const seriesOverrideId = form.selectedEref?.estimation_config?.series_override_id ?? null
	const { data: selectedModelNumberDetails } = useGetModelNumber(form.selectedModelNumber?.id ?? 0)
	const { data: seriesList } = useGetSeries()
	const missingPriceSeriesName = useMemo<string | null>(() => {
		if (seriesOverrideId === null) return null
		const details = selectedModelNumberDetails as TModelNumberDetails | undefined
		if (!details) return null
		if (details.pricing_info?.some((price) => price.series.id === seriesOverrideId)) return null
		return seriesList?.find((series) => series.id === seriesOverrideId)?.series_name ?? null
	}, [seriesOverrideId, selectedModelNumberDetails, seriesList])

	const { data: catalogueImage } = useGetModelNumberImageLink(form.selectedModelNumber?.id ?? 0)

	// First field of the form, so the shell puts the caret in it the moment the drag lands and the
	// user can carry straight on from the mouse without a extra click.
	const elevationFieldRef = useRef<HTMLDivElement>(null)

	const canCreate = !!area && form.isValid && !isPending

	const handleCreate = () => {
		if (!canCreate) return
		onCreate({
			relatedCategoryId: form.relatedCategoryId,
			width: form.width,
			height: form.height,
			depth: form.depth,
			selectedModelNumberId: form.selectedModelNumber?.id ?? null,
			eref: form.selectedEref ? { id: form.selectedEref.id, version: form.selectedEref.version } : null,
			// Null while the section is collapsed or incomplete, which is what keeps an untouched
			// section from adding a request (REQ-413/414).
			customization: form.customization.payload,
		})
	}

	return (
		<CreateAreaSidebar
			title="Object"
			hint="Drag on the drawing to define the object area"
			area={area}
			isPending={isPending}
			canSubmit={canCreate}
			submitLabel="Create object"
			onCancel={onCancel}
			onClose={onClose}
			onSubmit={handleCreate}
			focusTargetRef={elevationFieldRef}
			error={form.modelNumberError ?? undefined}
		>
			<div className={body}>
				<FurnitureImage
					imageUrl={catalogueImage?.download_url ?? null}
					isCountertop={false}
					hasModelNumber={form.selectedModelNumber !== null}
				/>

				<div ref={elevationFieldRef}>
					<ElevationReferenceSelect
						erefs={pageErefs}
						value={form.selectedErefId}
						onChange={form.setSelectedErefId}
						required={false}
						placeholder="Select elevation"
					/>
				</div>

				<div className={divider} />

				<ModelNumberSection
					furniture={modelNumberTarget}
					selectedModelNumber={form.selectedModelNumber}
					seriesOverrideId={seriesOverrideId}
					missingPriceSeriesName={missingPriceSeriesName}
					// There is no furniture to move yet, so `useArbitraryEndpoint` has no meaning here —
					// this panel owns `related_category_id` and simply sends the node the pick belongs to.
					onSelect={async (model, { categoryNodeId }) => form.selectModelNumber(model, categoryNodeId)}
					isRequired={false}
				/>

				<div className={divider} />

				<DimensionsSection
					width={form.width}
					height={form.height}
					depth={form.depth}
					onWidthChange={form.setWidth}
					onHeightChange={form.setHeight}
					onDepthChange={form.setDepth}
					isRequired={false}
					min={MIN_DIMENSION_IN}
				/>

				<div className={divider} />

				<CustomizationSection
					isExpanded={form.customization.isExpanded}
					note={form.customization.note}
					priceAdjustment={form.customization.priceAdjustment}
					onExpand={form.customization.expand}
					onRemove={form.customization.collapse}
					onNoteChange={form.customization.setNote}
					onPriceAdjustmentChange={form.customization.setPriceAdjustment}
				/>

				<div className={divider} />

				<CategoriesSection
					topLevelValue={categorySelection.selectedTopLevelCategory}
					topLevelOptions={categorySelection.topLevelOptions}
					onTopLevelChange={categorySelection.handleTopLevelChange}
					superValue={categorySelection.selectedSuperCategory}
					superOptions={categorySelection.superCategoryOptions}
					onSuperChange={categorySelection.handleSuperCategoryChange}
					modelCategoryValue={categorySelection.selectedModelCategory}
					modelCategoryOptions={categorySelection.modelCategoryOptions}
					onModelCategoryChange={categorySelection.handleModelCategoryChange}
				/>
			</div>
		</CreateAreaSidebar>
	)
}
